"use client";

/**
 * UserAvatar.tsx
 * REQ-028: User Profile UI - Avatar component with initials fallback
 *
 * Displays user avatar image when available, falls back to deterministic
 * colored initials based on djb2 hash of the user's name.
 */

import * as React from "react";
import Avatar from "@mui/material/Avatar";
import type { SxProps, Theme } from "@mui/material/styles";

export interface UserAvatarProps {
  /** User's first name */
  firstName: string | null;
  /** User's last name */
  lastName: string | null;
  /** URL of user's avatar image */
  avatarUrl: string | null;
  /** Avatar size: small=32px, medium=40px, large=72px */
  size?: "small" | "medium" | "large";
  /** Additional MUI sx props */
  sx?: SxProps<Theme>;
  /** Test ID for testing */
  "data-testid"?: string;
}

const sizeMap: Record<"small" | "medium" | "large", number> = {
  small: 32,
  medium: 40,
  large: 72,
};

/**
 * djb2 hash function for deterministic color generation.
 * Produces a consistent hash value for a given string.
 */
function djb2Hash(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) + str.charCodeAt(i);
  }
  return Math.abs(hash);
}

/**
 * Generate a deterministic HSL color from a name string.
 * Uses djb2 hash to ensure the same name always gets the same color.
 */
function getInitialsColor(firstName: string, lastName: string): string {
  const key = (firstName + lastName).trim().toLowerCase();
  if (key === "") {
    return "hsl(0, 65%, 45%)";
  }
  const hash = djb2Hash(key);
  const hue = hash % 360;
  return `hsl(${hue}, 65%, 45%)`;
}

/**
 * Get initials from first and last name.
 * Returns first letter of each name, uppercase.
 */
function getInitials(
  firstName: string | null,
  lastName: string | null
): string {
  const first = firstName?.charAt(0)?.toUpperCase() ?? "";
  const last = lastName?.charAt(0)?.toUpperCase() ?? "";
  return first + last;
}

/**
 * UserAvatar Component
 *
 * Displays a user avatar image, or falls back to initials with a
 * deterministic background color based on the user's name.
 *
 * @example
 * ```tsx
 * // With avatar image
 * <UserAvatar firstName="John" lastName="Doe" avatarUrl="https://..." />
 *
 * // With initials fallback
 * <UserAvatar firstName="John" lastName="Doe" avatarUrl={null} />
 *
 * // Different sizes
 * <UserAvatar firstName="Jane" lastName="Smith" avatarUrl={null} size="large" />
 * ```
 */
export function UserAvatar({
  firstName,
  lastName,
  avatarUrl,
  size = "medium",
  sx,
  "data-testid": testId = "user-avatar",
}: UserAvatarProps): React.JSX.Element {
  const dimension = sizeMap[size];
  const initials = getInitials(firstName, lastName);
  const bgColor = getInitialsColor(firstName ?? "", lastName ?? "");

  // Font size scales with avatar size
  const fontSize = dimension * 0.4;

  return (
    <Avatar
      src={avatarUrl ?? undefined}
      alt={initials || undefined}
      data-testid={testId}
      sx={{
        width: dimension,
        height: dimension,
        fontSize,
        ...(avatarUrl
          ? {}
          : {
              bgcolor: bgColor,
              backgroundColor: bgColor,
            }),
        ...((sx ?? {}) as Record<string, unknown>),
      }}
      style={{
        width: `${dimension}px`,
        height: `${dimension}px`,
        ...(avatarUrl ? {} : { backgroundColor: bgColor }),
      }}
    >
      {avatarUrl ? null : initials || null}
    </Avatar>
  );
}
